// src/utils/scadenzePagamenti.js — copia identica di functions/scadenzePagamenti.js
// (le functions non possono importare da src; cambia solo l'ultima riga, export/module.exports).
// La prova demo/prove/prova_scadenze_pagamenti.mjs controlla che diano lo stesso risultato.
// ═══════════════════════════════════════════════════════════════════════════
// PAGAMENTI DA CONTROLLARE (ottobre 2026, W432) — «non deve mai accadere che un bonifico
// non arrivi e nessuno se ne accorga» (Paolo)
// ═══════════════════════════════════════════════════════════════════════════
// Un solo elenco, usato in tre posti: il riquadro rosso in cima al pannello Owner, le etichette
// nelle liste di offerte e proforma, l'email di riepilogo ogni mattina (controlloPagamenti).
// Cosa entra:
//  • proforma e offerte inviate o accettate e non pagate: «in scadenza» da 2 giorni prima,
//    «scaduta» dal giorno dopo la scadenza, finché non sono segnate pagate o annullate;
//  • formazioni a pagamento entro 7 giorni senza un'offerta pagata collegata (anche se
//    confermate a mano: l'incontro non deve arrivare senza pagamento registrato);
//  • abbonamenti attivi con il periodo pagato già finito.
// Solo calcoli, nessun accesso al database.
// ═══════════════════════════════════════════════════════════════════════════
const A_PAGAMENTO_F = ["videocall", "presenza", "ora"];
const GIORNI_PREAVVISO = 2, GIORNI_FORMAZIONE = 7;
const giornoUtc = (s) => { const [a, m, g] = String(s).split("-").map(Number); return Date.UTC(a, m - 1, g); };
const giorniDa = (oggi, data) => Math.round((giornoUtc(data) - giornoUtc(oggi)) / 86400000);
const DATA_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Stato di pagamento di un documento (proforma o offerta): "" | "in_scadenza" | "scaduta". */
/** Scadenza del pagamento; un documento senza data (offerta senza incontro fissato) vale 15 giorni dall'emissione. */
function scadenzaEffettiva(d) {
  if (DATA_RE.test(d.scadenzaPagamento || "")) return d.scadenzaPagamento;
  if (DATA_RE.test(d.dataEmissione || "")) return new Date(giornoUtc(d.dataEmissione) + 15 * 86400000).toISOString().slice(0, 10);
  return "";
}
function statoScadenza(d, oggi) {
  if (!d || !["inviata", "accettata"].includes(d.stato) || !scadenzaEffettiva(d)) return "";
  const g = giorniDa(oggi, scadenzaEffettiva(d));
  if (g < 0) return "scaduta";
  if (g <= GIORNI_PREAVVISO) return "in_scadenza";
  return "";
}

/**
 * @param p { proforma: [{ id, ... }], offerte, prenotazioni, orgs, oggi }
 * @returns [{ chiave, tipo, id, livello: "scaduto" | "in_scadenza" | "senza_pagamento", testo, cliente, importo, scadenza, giorni }]
 *   in ordine di gravità (prima i più vecchi scaduti)
 */
function pagamentiDaControllare({ proforma = [], offerte = [], prenotazioni = [], orgs = [], oggi }) {
  const out = [];
  proforma.forEach(p => {
    const s = statoScadenza(p, oggi);
    if (!s) return;
    const g = giorniDa(oggi, scadenzaEffettiva(p));
    out.push({ chiave: `proforma_${p.id}`, tipo: "proforma", id: p.id, livello: s === "scaduta" ? "scaduto" : "in_scadenza", cliente: (p.cliente && p.cliente.ragioneSociale) || "", importo: p.totale || 0,
      scadenza: scadenzaEffettiva(p), giorni: g, testo: `Proforma ${p.numero || ""} (abbonamento)` });
  });
  const offertePagate = new Set();
  offerte.forEach(o => { if (o.stato === "pagata" && o.prenotazioneId) offertePagate.add(o.prenotazioneId); });
  offerte.forEach(o => {
    const s = statoScadenza(o, oggi);
    if (!s) return;
    out.push({ chiave: `offerta_${o.id}`, tipo: "offerta", id: o.id, livello: s === "scaduta" ? "scaduto" : "in_scadenza", cliente: (o.cliente && o.cliente.ragioneSociale) || "", importo: o.totale || 0,
      scadenza: scadenzaEffettiva(o), giorni: giorniDa(oggi, scadenzaEffettiva(o)), testo: `Offerta ${o.numero || ""} (formazione)` });
  });
  prenotazioni.forEach(p => {
    if (!p || p.stato === "annullata" || !A_PAGAMENTO_F.includes(p.formula) || !DATA_RE.test(p.data || "")) return;
    const g = giorniDa(oggi, p.data);
    if (g < 0 || g > GIORNI_FORMAZIONE || offertePagate.has(p.id)) return;
    out.push({ chiave: `formazione_${p.id}`, tipo: "formazione", id: p.id, livello: "senza_pagamento", cliente: p.orgNome || "", importo: 0, scadenza: p.data, giorni: g,
      testo: `Formazione del ${p.data.split("-").reverse().join("/")} alle ${p.inizio || ""} senza pagamento registrato${p.stato === "confermata" ? " (già confermata)" : ""}` });
  });
  orgs.forEach(o => {
    if (!o || o.status !== "active" || !DATA_RE.test(o.pianoPagatoFino || "")) return;
    const g = giorniDa(oggi, o.pianoPagatoFino);
    if (g >= 0) return;
    out.push({ chiave: `abbonamento_${o.id}`, tipo: "abbonamento", id: o.id, livello: "scaduto", cliente: o.name || o.nome || "", importo: 0, scadenza: o.pianoPagatoFino, giorni: g,
      testo: `Abbonamento scaduto il ${o.pianoPagatoFino.split("-").reverse().join("/")}, non rinnovato` });
  });
  const peso = { scaduto: 0, senza_pagamento: 1, in_scadenza: 2 };
  return out.sort((a, b) => peso[a.livello] - peso[b.livello] || a.giorni - b.giorni);
}

/** Riga descrittiva: «scaduta da 3 giorni», «scade domani», «tra 5 giorni»… */
function quando(x) {
  const g = x.giorni;
  if (x.livello === "senza_pagamento") return g === 0 ? "l'incontro è oggi" : g === 1 ? "l'incontro è domani" : `l'incontro è tra ${g} giorni`;
  if (g < 0) return `scaduto da ${-g} ${g === -1 ? "giorno" : "giorni"}`;
  if (g === 0) return "scade oggi";
  if (g === 1) return "scade domani";
  return `scade tra ${g} giorni`;
}

/**
 * Bozza della proforma di rinnovo: per gli abbonamenti attivi che finiscono entro 30 giorni e
 * non hanno ancora una proforma (non annullata) che parte dal giorno dopo la fine.
 * @returns [{ orgId, dal }] — i dati del cliente e il prezzo li completa l'app (stesso listino del sito)
 */
function rinnoviDaPreparare({ orgs = [], proforma = [], oggi, giorni = 30 }) {
  const out = [];
  orgs.forEach(o => {
    if (!o || o.status !== "active" || !DATA_RE.test(o.pianoPagatoFino || "")) return;
    const g = giorniDa(oggi, o.pianoPagatoFino);
    if (g > giorni) return;
    const dal = new Date(giornoUtc(o.pianoPagatoFino) + 86400000).toISOString().slice(0, 10);
    if (proforma.some(p => p.orgId === o.id && p.stato !== "annullata" && p.tipo !== "integrazione" && p.dal === dal)) return;
    out.push({ orgId: o.id, dal });
  });
  return out;
}

export { statoScadenza, scadenzaEffettiva, pagamentiDaControllare, quando, rinnoviDaPreparare, GIORNI_PREAVVISO, GIORNI_FORMAZIONE };
